import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertCircle, Loader2, Download, RefreshCw, ChevronDown, ZoomIn, ZoomOut } from "lucide-react";
import { useLoader } from "@/contexts/LoaderContext";
import { useJourney } from "@/contexts/JourneyContext";
import { toast } from "sonner";
import axios from 'axios';
import { API_BASE_URL } from '@/config';
import JourneySectionHeader from "@/components/JourneySectionHeader";
import { JourneyProgressSection } from "@/apps/los/components/ProgressBar/JourneySectionProgress";
import PdfCanvasViewer, { MIN_ZOOM, MAX_ZOOM } from "./PdfCanvasViewer";

interface CaprionAgreementPdfProps {
    isBuilderMode?: boolean;
    onNext?: () => Promise<void>;
    /** Passed by JourneyPage to every fullPageComponent — drives the header progress bar. */
    progressSections?: JourneyProgressSection[];
    currentPageId?: number | null;
}

// Polling configuration
const POLL_INTERVAL_MS = 5000; // Poll every 5 seconds
const MAX_POLLS = 60; // 60 × 5s = 300s (5 minutes) max wait time

const CaprionAgreementPdf: React.FC<CaprionAgreementPdfProps> = ({
    isBuilderMode = false,
    onNext,
    progressSections,
    currentPageId,
}) => {
    const { setLoading } = useLoader();
    const { refresh: refreshJourney } = useJourney();
    
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const [pdfBase64, setPdfBase64] = useState<string | null>(null);
    const [fetchingPdf, setFetchingPdf] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [accepted, setAccepted] = useState(false);
    const [taskId, setTaskId] = useState<string | null>(null);
    const [elapsedTime, setElapsedTime] = useState(0);
    const [pollCount, setPollCount] = useState(0);

    // Reading progress inside the embedded PDF (0-100). Drives the thin progress
    // bar and the "scroll to end" affordance.
    const [readProgress, setReadProgress] = useState(0);
    const [reachedEnd, setReachedEnd] = useState(false);
    // True once every PDF page has been painted. The Scroll pill and the
    // accept/eSign block are held back until then — showing them over a blank,
    // still-rasterising viewport lets the user act before seeing the agreement.
    const [pdfRendered, setPdfRendered] = useState(false);
    // True when rasterisation failed outright (e.g. an older mobile browser
    // missing Promise.withResolvers, which pdfjs-dist v4 needs internally).
    //
    // Tracked SEPARATELY from pdfRendered because the two need different layouts:
    // on success the accept/eSign block sits at the very END of the document so
    // the user must scroll through it first (with the Scroll pill to help), but on
    // failure there is nothing to scroll — so the block is rendered directly under
    // the error message and the Scroll pill is suppressed. Without this split the
    // borrower was stranded: error on screen, controls hidden, no way forward.
    const [pdfFailed, setPdfFailed] = useState(false);
    // PDF zoom lives here (not in PdfCanvasViewer) so the controls can be an
    // overlay pinned to the viewport — inside the viewer they would be centred
    // within the zoomed page width and end up off-screen.
    const [pdfZoom, setPdfZoom] = useState(1);

    const startedRef = useRef(false);
    const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const elapsedIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    // Only the PDF scrolls — the header and the eSign action bar are pinned, so
    // the button is always on screen and never has to be scrolled to.
    const pdfScrollRef = useRef<HTMLDivElement>(null);
    // The accept/eSign block at the end of the document. Used to decide when the
    // floating Scroll pill has served its purpose and must disappear.
    const pdfActionsRef = useRef<HTMLDivElement>(null);

    // Cleanup intervals on unmount
    useEffect(() => {
        return () => {
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
            }
            if (elapsedIntervalRef.current) {
                clearInterval(elapsedIntervalRef.current);
            }
        };
    }, []);

    // Start elapsed time counter
    const startElapsedTimer = () => {
        setElapsedTime(0);
        elapsedIntervalRef.current = setInterval(() => {
            setElapsedTime(prev => prev + 1);
        }, 1000);
    };

    // Stop elapsed time counter
    const stopElapsedTimer = () => {
        if (elapsedIntervalRef.current) {
            clearInterval(elapsedIntervalRef.current);
            elapsedIntervalRef.current = null;
        }
    };

    // Poll for task status
    const pollTaskStatus = async (taskId: string) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/caprion/get-agreement-pdf-status/${taskId}/`);
            
            if (response.data.status === 'success') {
                // Task completed successfully
                stopElapsedTimer();
                if (pollIntervalRef.current) {
                    clearInterval(pollIntervalRef.current);
                    pollIntervalRef.current = null;
                }
                
                setPdfBase64(response.data.pdf_base64);
                setFetchingPdf(false);
                toast.success("Agreement document loaded successfully");
                
            } else if (response.data.status === 'error') {
                // Task failed
                stopElapsedTimer();
                if (pollIntervalRef.current) {
                    clearInterval(pollIntervalRef.current);
                    pollIntervalRef.current = null;
                }
                
                const errorMsg = response.data.error || "Failed to load agreement PDF";
                setError(errorMsg);
                setFetchingPdf(false);
                toast.error(errorMsg);
                
            } else if (response.data.status === 'processing') {
                // Still processing - continue polling
                setPollCount(prev => prev + 1);
                
                // Check if we've exceeded max polls
                if (pollCount >= MAX_POLLS) {
                    stopElapsedTimer();
                    if (pollIntervalRef.current) {
                        clearInterval(pollIntervalRef.current);
                        pollIntervalRef.current = null;
                    }
                    
                    setError("PDF generation is taking longer than expected. Please try again.");
                    setFetchingPdf(false);
                    toast.error("Request timed out. Please try again.");
                }
            }
        } catch (error: any) {
            console.error("Poll Status Error:", error);
            
            // Check if it's an HTTP error (404, 500, etc.) - stop polling immediately
            if (error.response) {
                stopElapsedTimer();
                if (pollIntervalRef.current) {
                    clearInterval(pollIntervalRef.current);
                    pollIntervalRef.current = null;
                }
                
                const errorMsg = error.response?.data?.error || error.response?.data?.message || "Failed to load agreement PDF";
                setError(errorMsg);
                setFetchingPdf(false);
                toast.error(errorMsg);
                return; // Stop polling
            }
            
            // For network errors (no response), continue polling until max attempts
            if (pollCount >= MAX_POLLS) {
                stopElapsedTimer();
                if (pollIntervalRef.current) {
                    clearInterval(pollIntervalRef.current);
                    pollIntervalRef.current = null;
                }
                
                const errorMsg = "Network error. Please check your connection and try again.";
                setError(errorMsg);
                setFetchingPdf(false);
                toast.error(errorMsg);
            }
        }
    };

    // Start PDF generation
    const startPdfGeneration = async () => {
        setFetchingPdf(true);
        setError(null);
        setPollCount(0);
        setElapsedTime(0);
        
        try {
            // Start the async task
            const response = await axios.post(`${API_BASE_URL}/api/caprion/get-agreement-pdf/`);
            
            if (response.data.success && response.data.status === 'success') {
                // Backend already had the agreement stored - no polling needed
                setPdfBase64(response.data.pdf_base64);
                setFetchingPdf(false);
                toast.success("Agreement document loaded successfully");
                
            } else if (response.data.success && response.data.task_id) {
                const newTaskId = response.data.task_id;
                setTaskId(newTaskId);
                
                // Start elapsed time counter
                startElapsedTimer();
                
                // Start polling for status
                pollIntervalRef.current = setInterval(() => {
                    pollTaskStatus(newTaskId);
                }, POLL_INTERVAL_MS);
                
                // Do first poll immediately
                pollTaskStatus(newTaskId);
                
            } else {
                throw new Error(response.data.error || "Failed to start PDF generation");
            }
        } catch (error: any) {
            console.error("Start PDF Generation Error:", error);
            const errorMsg = error.response?.data?.error || error.message || "Failed to start PDF generation";
            setError(errorMsg);
            setFetchingPdf(false);
            toast.error(errorMsg);
        }
    };

    // Initial load
    useEffect(() => {
        if (isBuilderMode) return;
        if (startedRef.current) return;
        
        startedRef.current = true;
        startPdfGeneration();
    }, [isBuilderMode]);

    // Retry handler
    const handleRetry = () => {
        // Clear any existing intervals
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
        stopElapsedTimer();
        
        // Reset state
        startedRef.current = false;
        setTaskId(null);
        setPollCount(0);
        // Reset reading progress so the new document is measured from scratch
        setReadProgress(0);
        setReachedEnd(false);
        
        // Restart
        startedRef.current = true;
        startPdfGeneration();
    };

    // base64 -> Blob. Mobile browsers (notably Chrome on Android) refuse large
    // data: URIs for downloads and plugin resources, so we always go via a Blob.
    const buildPdfBlob = (base64: string): Blob => {
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }
        return new Blob([bytes], { type: 'application/pdf' });
    };

    const handleDownload = () => {
        if (pdfUrl) {
            window.open(pdfUrl, '_blank');
            return;
        }
        
        if (!pdfBase64) {
            toast.error("PDF not available for download");
            return;
        }
        
        const url = URL.createObjectURL(buildPdfBlob(pdfBase64));
        const link = document.createElement('a');
        link.href = url;
        link.download = 'loan_agreement.pdf';
        link.click();
        // Give the browser a moment to start the download before revoking
        setTimeout(() => URL.revokeObjectURL(url), 10000);
    };

    const handleNext = async () => {
        if (!accepted) {
            toast.error("Please confirm that you have read the agreement");
            return;
        }
        
        // If onNext prop is provided (from JourneyPage), use it
        // Otherwise fall back to refreshJourney
        if (onNext) {
            await onNext();
        } else {
            setLoading(true);
            try {
                await refreshJourney();
                toast.success("Proceeding to agreement signing");
            } catch (error) {
                console.error("Failed to advance journey:", error);
                toast.error("Failed to proceed. Please try again.");
            } finally {
                setLoading(false);
            }
        }
    };

    // ---------------------------------------------------------------------
    // Reading-progress tracking for the embedded PDF
    // ---------------------------------------------------------------------

    const handlePdfScroll = useCallback(() => {
        const el = pdfScrollRef.current;
        if (!el) return;

        const scrollable = el.scrollHeight - el.clientHeight;
        if (scrollable <= 0) {
            // Document fits without scrolling — nothing left to read.
            setReadProgress(100);
            setReachedEnd(true);
            return;
        }

        const progress = Math.min(100, Math.round((el.scrollTop / scrollable) * 100));
        setReadProgress(progress);

        // The Scroll pill must vanish as soon as the accept/eSign block scrolls
        // into view — not only at the very last pixel. A plain
        // "scrollTop + clientHeight >= scrollHeight - 24" check kept the pill on
        // screen over the last page because of the tail spacer below the block.
        const actionBlockTop = pdfActionsRef.current?.offsetTop;
        if (typeof actionBlockTop === 'number') {
            setReachedEnd(el.scrollTop + el.clientHeight >= actionBlockTop + 8);
            return;
        }
        // 24px tolerance: sub-pixel scroll heights mean scrollTop rarely equals
        // scrollable exactly, especially on mobile with elastic scrolling.
        setReachedEnd(el.scrollTop + el.clientHeight >= el.scrollHeight - 24);
    }, []);

    // Manual "jump to end" — for users who don't want to scroll the whole doc.
    const scrollPdfToEnd = () => {
        const el = pdfScrollRef.current;
        if (!el) return;
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    };

    // Format elapsed time as MM:SS
    const formatElapsedTime = (seconds: number): string => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    if (isBuilderMode) {
        return (
            <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 text-center text-sm text-gray-400">
                <div className="text-2xl mb-1">📄</div>
                <p className="font-semibold text-gray-500">Caprion Agreement PDF</p>
                <p className="text-xs mt-1">Display loan agreement PDF before signing</p>
            </div>
        );
    }

    return (
        // Viewport-locked column: header + action bar are pinned and only the PDF
        // scrolls. h-[100dvh] (not min-h-screen) keeps the eSign button on screen
        // on mobile, where the browser chrome makes 100vh taller than the viewport.
        <div className="h-[100dvh] overflow-hidden bg-gray-50 flex flex-col">
            {/* Header — page title + journey progress, exactly like JourneyPage's
                own header on the Personal/Business Information pages (bold title
                above the progress bar). Replaces the logo row so the agreement
                gets as much of the viewport as possible; on a phone every row
                counts. */}
            <JourneySectionHeader
                className="shrink-0"
                title="Loan Agreement Document"
                progressSections={progressSections}
                currentPageId={currentPageId}
            />

            {/* Loading State with Progress */}
            {fetchingPdf && (
                <div className="flex-1 flex items-center justify-center p-4">
                    <div className="text-center max-w-md">
                        <Loader2 className="w-12 h-12 text-intellend-green animate-spin mx-auto mb-4" />
                        <h2 className="text-xl font-semibold text-gray-900 mb-2">Generating Agreement Document</h2>
                        <p className="text-gray-500 mb-4">Please wait while we prepare your loan agreement...</p>
                        
                        {/* Progress Indicator */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                            <div className="flex items-center justify-center space-x-2 mb-2">
                                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                                <span className="text-sm font-medium text-blue-700">Processing...</span>
                            </div>
                            <p className="text-xs text-blue-600">
                                Elapsed time: {formatElapsedTime(elapsedTime)}
                            </p>
                        </div>
                        
                        {/* Info message for long wait */}
                        {elapsedTime > 15 && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                                <p className="text-xs text-yellow-700">
                                    ⏳ This may take up to 2 minutes. Please don't close this page.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Error State */}
            {error && !fetchingPdf && (
                <div className="flex-1 flex items-center justify-center p-4">
                    <div className="text-center max-w-md">
                        <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                            <AlertCircle className="w-8 h-8" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Failed to Load Agreement</h2>
                        <p className="text-gray-500 mb-6">{error}</p>
                        <Button
                            onClick={handleRetry}
                            className="bg-intellend-green hover:bg-intellend-green/90"
                        >
                            <RefreshCw className="mr-2 h-4 w-4" />
                            Try Again
                        </Button>
                    </div>
                </div>
            )}

            {/* Success State - PDF Loaded */}
            {!fetchingPdf && !error && (pdfUrl || pdfBase64) && (
                <>
                    {/* PDF Viewer — takes all remaining height. min-h-0 is required
                        so the inner scroll area can shrink inside the flex column
                        instead of pushing the action bar off screen. */}
                    <div className="relative flex-1 min-h-0 bg-white flex flex-col">
                        {/* Reading progress of the embedded document. Tells the user
                            how much is left instead of leaving them guessing.
                            Hidden when rendering failed — there is no document to
                            track, and a bar frozen at 0% next to the error reads
                            like something is still loading. */}
                        {!pdfUrl && pdfBase64 && !pdfFailed && (
                            <div className="shrink-0 h-1 w-full bg-gray-200">
                                <div
                                    className="h-full bg-intellend-green transition-[width] duration-150 ease-out"
                                    style={{ width: `${readProgress}%` }}
                                />
                            </div>
                        )}

                        {/* Hosted agreement link (if the lender gave us a URL) */}
                        {pdfUrl && (
                            <iframe
                                src={pdfUrl}
                                className="flex-1 min-h-0 w-full"
                                title="Loan Agreement PDF"
                            />
                        )}

                        {/* Canvas-rendered PDF - works on every mobile browser,
                            unlike an <iframe> pointed at a data: PDF URI.
                            The scroll container lives here (not inside the viewer) so we
                            can track how far the user has read. */}
                        {!pdfUrl && pdfBase64 && (
                            <div className="relative flex-1 min-h-0">
                                <div
                                    ref={pdfScrollRef}
                                    onScroll={handlePdfScroll}
                                    // overflow-x-auto so the user can pan sideways once the
                                    // document is pinch-zoomed wider than the viewport.
                                    className="h-full w-full overflow-y-auto overflow-x-auto overscroll-contain"
                                >
                                    <PdfCanvasViewer
                                        base64={pdfBase64}
                                        className="w-full"
                                        // Canvases are appended asynchronously — re-measure once
                                        // rendering finishes so short documents count as "read",
                                        // and only then reveal the Scroll/eSign controls.
                                        onRendered={() => { setPdfRendered(true); handlePdfScroll(); }}
                                        // A render failure must ALSO unlock the controls, otherwise
                                        // the borrower is stranded: the viewer shows "Failed to Load
                                        // PDF" while the accept checkbox and eSign button stay
                                        // hidden, leaving no way forward. They can still read the
                                        // document via the Download button. pdfFailed (not
                                        // pdfRendered) is set so the block renders right under the
                                        // error instead of after a document that isn't there.
                                        onError={() => { setPdfFailed(true); }}
                                        zoom={pdfZoom}
                                        onZoomChange={setPdfZoom}
                                    />

                                    {/* Accept + eSign live at the END of the document, inside the
                                        scroll area: the user must reach the last line before they
                                        can act on it, which is the point of showing the agreement.
                                        Previously this was a fixed bar pinned over the document.
                                        Hidden until rasterisation finishes so it never appears
                                        before the agreement itself is on screen. */}
                                    {(pdfRendered || pdfFailed) && (
                                    <div ref={pdfActionsRef} className="border-t bg-white px-4 py-4">
                                        <div className="mx-auto max-w-4xl">
                                            <div className="mb-3 flex items-start space-x-3">
                                                <input
                                                    type="checkbox"
                                                    id="agreement-acceptance"
                                                    checked={accepted}
                                                    onChange={(e) => setAccepted(e.target.checked)}
                                                    className="mt-0.5 h-5 w-5 rounded border-gray-300 text-intellend-green focus:ring-intellend-green"
                                                />
                                                <label htmlFor="agreement-acceptance" className="cursor-pointer text-xs text-gray-700">
                                                    I have read and understood this legally binding loan agreement
                                                    and am ready to sign it.
                                                </label>
                                            </div>
                                            <Button
                                                onClick={handleNext}
                                                disabled={!accepted}
                                                className="h-12 w-full bg-intellend-green text-base font-semibold hover:bg-intellend-green/90"
                                            >
                                                eSign Agreement
                                            </Button>
                                        </div>
                                    </div>
                                    )}

                                    {/* Tail spacer: lifts the eSign button clear of the bottom
                                        edge (and of the floating Scroll pill) once the user has
                                        scrolled all the way down. */}
                                    {/* Only needed when a real document was painted: with no
                                        pages there is nothing to lift the button clear of, and
                                        the spacer would just add dead scroll under the error. */}
                                    {pdfRendered && !pdfFailed && <div className="h-24" />}
                                </div>

                                {/* Zoom controls — vertical stack pinned to the top-right of the
                                    viewport (not inside the scroll content) so they stay
                                    reachable when the page is zoomed wider than the screen.
                                    Pinch works too, but is not discoverable and does not exist
                                    on desktop. */}
                                {/* Download — cutout pill at the top-right of the document.
                                    Separate control from zoom, which sits just below it. */}
                                <button
                                    type="button"
                                    onClick={handleDownload}
                                    aria-label="Download agreement"
                                    title="Download agreement"
                                    className="absolute right-3 top-3 z-10 flex items-center gap-1.5 rounded-xl bg-gray-900 px-3 py-2 text-xs font-semibold text-white shadow-lg transition-colors hover:bg-gray-800 active:bg-gray-950"
                                >
                                    <Download className="h-4 w-4" />
                                    Download
                                </button>

                                <div className="absolute top-16 right-3 flex flex-col items-center gap-0.5 rounded-full border border-gray-200 bg-white/90 px-1 py-1.5 shadow-md backdrop-blur-sm">
                                    <button
                                        type="button"
                                        onClick={() => setPdfZoom(z => Math.min(MAX_ZOOM, Math.round((z + 0.5) * 10) / 10))}
                                        disabled={pdfZoom >= MAX_ZOOM}
                                        aria-label="Zoom in"
                                        className="flex h-8 w-8 items-center justify-center rounded-full text-gray-700 disabled:opacity-40"
                                    >
                                        <ZoomIn className="h-4 w-4" />
                                    </button>
                                    <span className="text-[10px] font-medium leading-none text-gray-600">
                                        {Math.round(pdfZoom * 100)}%
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => setPdfZoom(z => Math.max(MIN_ZOOM, Math.round((z - 0.5) * 10) / 10))}
                                        disabled={pdfZoom <= MIN_ZOOM}
                                        aria-label="Zoom out"
                                        className="flex h-8 w-8 items-center justify-center rounded-full text-gray-700 disabled:opacity-40"
                                    >
                                        <ZoomOut className="h-4 w-4" />
                                    </button>
                                </div>

                                {/* Floating "Scroll" jump-to-end pill. Appears once the document
                                    is rendered, stays visible while reading, and disappears at
                                    the end where the accept checkbox + eSign button are.
                                    Gold outline + gold text on a soft gold-tinted fill, so the
                                    pill reads as a warm accent rather than a solid CTA. */}
                                {pdfRendered && !pdfFailed && !reachedEnd && (
                                    <button
                                        type="button"
                                        onClick={scrollPdfToEnd}
                                        aria-label="Scroll to end of agreement"
                                        className="absolute bottom-24 left-1/2 -translate-x-1/2 flex items-center gap-1.5 rounded-full border-2 border-intellend-gold bg-intellend-gold/15 px-4 py-2 text-sm font-semibold text-intellend-gold shadow-lg backdrop-blur-sm"
                                    >
                                        Scroll
                                        <ChevronDown className="h-4 w-4" />
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Hosted-PDF (iframe) path keeps a fixed action bar, because an
                        iframe's internal scroll position is not observable — we cannot
                        place the controls at the document's end there. */}
                    {pdfUrl && (
                        <div className="shrink-0 border-t bg-white px-4 py-3">
                            <div className="mx-auto max-w-4xl">
                                <div className="mb-2 flex items-start space-x-3">
                                    <input
                                        type="checkbox"
                                        id="agreement-acceptance-hosted"
                                        checked={accepted}
                                        onChange={(e) => setAccepted(e.target.checked)}
                                        className="mt-0.5 h-5 w-5 rounded border-gray-300 text-intellend-green focus:ring-intellend-green"
                                    />
                                    <label htmlFor="agreement-acceptance-hosted" className="cursor-pointer text-xs text-gray-700">
                                        I have read and understood this legally binding loan agreement
                                        and am ready to sign it.
                                    </label>
                                </div>
                                <Button
                                    onClick={handleNext}
                                    disabled={!accepted}
                                    className="h-12 w-full bg-intellend-green text-base font-semibold hover:bg-intellend-green/90"
                                >
                                    eSign Agreement
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default CaprionAgreementPdf;
